import { ProductPermissions } from '../constants/constants';

export interface Error {
  code: string;
  message: string;
}

export interface APIError {
  traceId: string;
  requestId: string;
  errors: Error[];
}

// Defines the promotion rule data used to control the Days Extension field.
export interface AppliedPromotionRule {
  parameterKey: string;
  promotionValue: string;
}

export interface PrefundCardDTO {
  readonly totalFundingAmount: string;
  readonly storeNumber: string;
  readonly employeeId: string;
  readonly approvalLevel: string;
  readonly vendorName: string;
  readonly globalCustomerId: string;
  readonly spendOrigin: string;
  readonly products: FundingProductInfo[];
}

export interface FundingProductInfo {
  readonly modelNumber: string;
  readonly itemNumber: string;
  readonly amount: string;
  readonly quantity: string;
  readonly weeklyRate: string;
  readonly weeklyTerm: string;
  readonly cashPrice: string;
  readonly totalCost: string;
}

export interface ShopLocalProductUI {
  selectedDepartment: ComboBoxData;
  selectedSubDepartment: ComboBoxData;
  selectedBraket: ComboBoxData;
  departmentOptions: ComboBoxData[];
  bracketOptions: ComboBoxData[];
  subdepartmentOptions: ComboBoxData[];
  productNameInput: string;
  quantityUI: string;
  unitPriceInputUI: string;
  quantity: string;
  unitPriceInput: string;
  typeSelected: string;
  subTypeSelected: string;
  rmsItemNumber: string;
  productPricing: ProductPricing;
}
export interface ProductPricing {
  weeklyRate: string;
  cashPrice: string;
  errMsg: string;
  priceWithFudgeFactor: string;
  rmsItemNumber: string;
  totalCost: string;
  weeklyTerm: string;
}
export interface PricingData {
  id: string;
  name: string;
}
export interface ComboBoxData {
  value: string;
  label: string;
}
export enum SpendOrigins {
  ONLINE = 'ONLINE',
  PHYSICAL = 'PHYSICAL',
}
export interface FundingRequest {
  totalAmount: number;
  storeNumber: string;
  employeeId: string;
  approvalLevel: string;
  vendorName: string;
  globalCustomerId: string;
  spendOrigin: SpendOrigins;
  products: [
    {
      amount: number;
      quantity: number;
      modelNumber: string;
    }
  ];
}

export interface User {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly route: string;
  readonly permissions: ProductPermissions[];
}

export interface Action<T1 = any, T2 = any> {
  type: T1;
  payload?: T2;
}

export interface GroupStoreConfigResponse {
  configDetails: ConfigDetails[];
}
export interface ConfigDetails {
  storeNumber: string;
  storeConfigDetails: StoreConfigDetails[];
}
export interface StoreConfigDetails {
  paramValue: string;
  paramKeyName: string;
  paramGroupName: string;
  paramCategoryName: string;
  paramKeyId: string;
  paramHierarchy: string;
  paramConfigs: ParamConfigDetails[];
}
export interface ParamConfigDetails {
  configName: string;
  configValue: string;
  configNameDesc: string;
}

export interface CustomerLocationState {
  customer: any;
  customerIndex: number;
}

export interface LSLocationItem {
  rmsItemNumber?: string;
  itemNumber?: string;
  weeklyRate?: string;
  weeklyTerm?: string;
  cashPrice?: string;
  model?: string;
  brand?: string;
  brandName?: string;
  amount?: string;
  quantity?: string;
  totalCost?: string;
  costPerItem?: string;
  modelNumber?: string;
  fundingRequestItemId?: string;
  department?: string;
  subDepartment?: string;
  bracket?: string;
  itemType?: string;
  subType?: string;
  departmentId?: string;
  subDepartmentId?: string;
  bracketId?: string;
  itemTypeId?: string;
  subTypeId?: string;
  description?: string;
}

export interface LSLocationState {
  spendLimitId?: string;
  storeNumber?: string;
  vendorName?: string;
  vendorId?: string | null;
  supplierName?: string;
  supplierOrderNumber?: string;
  model?: string;
  modelNumber?: string;
  brand?: string;
  amount?: string;
  costPerItem?: string;
  totalCost?: string;
  items?: LSLocationItem[];
}

// RP-668: Legal Price API request/response interfaces
export interface LegalPriceRequest {
  storeNumber: string;
  inventoryPriceId: string[];
}

export interface TotalLegalPrice {
  totalRate: string;
  totalTerm: string;
  totalCost: string;
  totalCashPrice: string;
}

export interface LegalInventoryDetail {
  inventoryId: string;
  inventoryPriceId: string;
  inventoryNumber: string;
  rate: string;
  term: string;
  totalCost: string;
  cashPrice: string;
}

export interface LegalPriceResponseData {
  totalLegalPrice: TotalLegalPrice;
  inventoryDetails: LegalInventoryDetail[];
}

export interface LegalPriceResponse {
  status: number;
  data: LegalPriceResponseData;
}

export interface SearchResultItem {
  rmsItemNumber?: string;
  itemNumber?: string;
  weeklyRate?: string;
  weeklyTerm?: string;
  cashPrice?: string;
  storeNumber?: string | null;
  model?: string | null;
  modelNumber?: string | null;
  brand?: string | null;
  costPerItem?: string | null;
  qty?: string;
  quantity?: string;
  wareHouseId?: string;
  [key: string]: unknown;
}

export interface CreateAgreementPayload {
  spendLimitId?: string | null;
  agreementSource?: string;
  vendorName?: string;
  vendorId?: string | null;
  supplierName?: string;
  supplierOrderNumber?: string | null;
  promoCode?: string | null;
  items?: Record<string, unknown>[];
  [key: string]: unknown;
}

export interface UseLocalSourcingParams {
  customerId: string;
  searchResult: SearchResultItem[] | undefined;
  setsearchResult: (val: SearchResultItem[]) => void;
  setagreementOptions: (val: unknown) => void;
  offerCodeValue: string;
}

export interface UseLocalSourcingReturn {
  isLocalSourcingFlow: boolean;
  applyLocalSourcingPayload: (
    payload: CreateAgreementPayload
  ) => CreateAgreementPayload;
}

export const EVENT_TYPES: Record<string, string> = {
  SE: 'Store Event',
  D: 'Delivery',
  CO: 'Carry Out',
  CI: 'CarryIn',
  S: 'Service',
  SCI: 'Service Carry In',
  SCO: 'Service Carry Out',
  STOPIC: 'Store Pick-up',
  FIELD: 'Field Visit',
  P: 'Pickup',
  G: 'General',
  SO: 'Switchout',
  SP: 'Service Pickup',
  TR: 'Transfer',
  TRD: 'Transfer Delivery',
  TRP: 'Transfer Pickup',
  AD: 'Acima Delivery',
};
export function getEventType(refCode: string): string | undefined {
  return EVENT_TYPES[refCode];
}

// DE (Decision Engine) Approval Amount enforcement — see utils/deEnforcementUtils.ts
export type DEOverrideRoles = Record<string, number>;

export interface DEEnforcementConfig {
  enabled: boolean;
  levelConfigs: Record<string, DEOverrideRoles>;
  invalidLevels?: string[];
  configurationError?: boolean;
  enforcedAgreementTypes: string[];
}

export interface DEEnforcementInput {
  config: DEEnforcementConfig;
  deStatus: string | null | undefined;
  verifyLevel: number | string | null | undefined;
  weeklyRate: number | string | null | undefined;
  totalCashPrice: number | string | null | undefined;
  weeklyApprovalAmount: number | string | null | undefined;
  approvalAmount: number | string | null | undefined;
  // Backend agreementType refCode (e.g. ItemSearchValues.Itemsearch[0].agreementType === 'RTO'
  // for Rental, 'SPEC' for Customer Order) — gated against config.enforcedAgreementTypes.
  agreementType?: string;
  // Rental's own agreementType stays 'RTO' during a SwitchOut (isExchangeAgreement), so it
  // can't be distinguished via agreementType alone — kept as the pre-existing separate signal.
  isSwitchOut?: boolean;
}

export type DEEnforcementStatus =
  | 'PASS'
  | 'BYPASS'
  | 'APPROVAL_REQUIRED'
  | 'HARD_BLOCK';

export interface DEEnforcementEvaluation {
  status: DEEnforcementStatus;
  reason?: string;
  exceedPercent: number;
  tier?: string;
  moduleName?: string;
  // Carried through so tag events (buildDEEnforcementTagEvent) don't need to re-resolve it.
  verifyLevel?: number | string | null;
}

export type DEEnforcementTagType =
  | 'ENFORCED_OVERRIDE'
  | 'ENFORCED_HARD_BLOCK'
  | 'BYPASS';

export interface DEEnforcementTagEventParams {
  tagType: DEEnforcementTagType;
  bypassReason?: string | null;
  overrideTier?: string | null;
  overridePercentage?: number | null;
  exceedPercent?: number | null;
  verifyLevel?: number | string | null;
  approverId?: string | number | null;
  approverRole?: string | null;
  authenticationMethod?: string | null;
  agreementNo?: string | number | null;
  customerId?: string | number | null;
  storeNo?: string | number | null;
  employeeId?: string | number | null;
  role?: string | null;
  currentScreen: string;
}
